import {useInfiniteQuery} from '@tanstack/react-query';

import {fetchRecords} from '../services/ReportRunsService';

// FNP-918: the data layer for the report runs list.
//
// Query keys are STRUCTURED and module-scoped (checklist §8). The previous key
// was `['reportRuns', currentToken]` — a bare string plus a page token, which
// both collides across modules and makes the cache page-shaped rather than
// list-shaped.
export const reportRunsKeys = {
    all: ['report-runs'] as const,
    lists: () => [...reportRunsKeys.all, 'list'] as const,
    list: (filters: Record<string, string>) =>
        [...reportRunsKeys.lists(), {filters}] as const,
    details: () => [...reportRunsKeys.all, 'detail'] as const,
    detail: (id: string) => [...reportRunsKeys.details(), id] as const,
};

// Page size is a SERVER concern under token pagination — it is the `limit` sent
// with the query, fixed by the module. There is deliberately no rows-per-page
// dropdown: there is no page to resize and no total to divide (checklist §9).
const PAGE_SIZE = 50;

/**
 * FNP-918: TOKEN PAGINATION.
 *
 * This was `useQuery` + the app-local `usePagination` token stack, which read
 * as compliant (a query hook, a page control, forward/back buttons) while being
 * a page-at-a-time model the shared footer cannot describe.
 *
 * ⚠️ `getNextPageParam` MUST return `undefined` — not `null`, and never the
 * same token back — when there are no more pages. Returning `null` leaves
 * `hasNextPage` true and react-query refetches forever.
 *
 * ⚠️ NO FILTER ARGUMENT IS SENT. `listReportRuns` declares only
 * `($limit: Int, $nextToken: String)` — see services/queries.ts. The old
 * service passed a `filter` variable anyway, which is an UNDECLARED GraphQL
 * variable: AppSync ignores it silently, so it looked like server-side
 * filtering while doing nothing at all. Filtering is client-side over the
 * loaded window; see useReportRuns.ts.
 */
export const useReportRunsInfiniteQuery = () => {
    return useInfiniteQuery({
        queryKey: reportRunsKeys.list({}),
        queryFn: ({pageParam}: {pageParam: string | null}) =>
            fetchRecords(PAGE_SIZE, pageParam ?? undefined),
        getNextPageParam: (lastPage) => lastPage?.nextToken ?? undefined,
        // Explicit annotation, or `pageParam` type-errors (§9).
        initialPageParam: null as string | null,
    });
};

// ⚠️ There is NO delete mutation here, and that is not an omission. The API
// exposes listReportRuns / getReportRun / createReportRun only — a report run
// is an immutable record of a job that was executed, so there is nothing to
// update and nothing to retract. See the selection note in RecordsTable.
